import { InsertLocationSchema } from "./types";
import z from "zod";
import { v4 as uuidv4 } from "uuid";
import { convertCmToMeters } from "~/lib/utils";

type Weekday = "mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun";

type Timeframe = {
  from:
  | 0
  | 1
  | 2
  | 3
  | 4
  | 5
  | 6
  | 7
  | 8
  | 9
  | 10
  | 11
  | 12
  | 13
  | 14
  | 15
  | 16
  | 17
  | 18
  | 19
  | 20
  | 21
  | 22
  | 23;
  to:
  | 1
  | 2
  | 3
  | 4
  | 5
  | 6
  | 7
  | 8
  | 9
  | 10
  | 11
  | 12
  | 13
  | 14
  | 15
  | 16
  | 17
  | 18
  | 19
  | 20
  | 21
  | 22
  | 23
  | 24
  | 25
  | 26
  | 27;
};

type RentalDate = {
  days: Weekday[];
  timeframes: Timeframe[];
};

type LocationData = Awaited<
  ReturnType<typeof import("~/lib/server-functions/get-locations").getLocation>
>;

const WEEKDAY_REVERSE_MAP: Record<
  number,
  "mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun"
> = {
  0: "mon",
  1: "tue",
  2: "wed",
  3: "thu",
  4: "fri",
  5: "sat",
  6: "sun",
};

// Helper to ensure bilingual fields always have string values
function normalizeBilingual(
  value: { german?: string | null; english?: string | null } | null | undefined,
): { german: string; english: string } {
  if (!value) {
    return { german: "", english: "" };
  }
  return {
    german: value.german ?? "",
    english: value.english ?? "",
  };
}

// Synthetic attribute for backfilling legacy boolean/count fields into additionalAttributes
function syntheticAttr(
  german: string,
  purpose: string,
): z.infer<typeof import("./types").AdditionalAttributeSchema> {
  return {
    tempId: uuidv4(),
    name: { german, english: "" },
    purpose,
    active: true,
  };
}

export function transformLocationDataToForm(
  locationData: NonNullable<LocationData>,
): z.infer<typeof InsertLocationSchema> {
  // This function transforms location data from the database format to the form schema format
  // Transform openingHours to rentalDates

  const byDay = new Map<Weekday, Timeframe[]>();

  if (locationData.openingHours) {
    for (const hour of locationData.openingHours) {
      const weekday = WEEKDAY_REVERSE_MAP[hour.weekday] as Weekday | undefined;
      if (!weekday || !hour.fromTime || !hour.untilTime) continue;

      const from = Math.max(
        0,
        Math.min(23, parseInt(hour.fromTime.split(":")[0]) || 0),
      ) as Timeframe["from"];

      const rawTo = parseInt(hour.untilTime.split(":")[0]) || 24;
      const to =
        rawTo === -1
          ? (-1 as Timeframe["to"])
          : (Math.max(1, Math.min(27, rawTo)) as Timeframe["to"]);

      const existing = byDay.get(weekday) ?? [];
      existing.push({ from, to });
      byDay.set(weekday, existing);
    }
  }

  // group by same timeframes and days
  const grouped = new Map<string, RentalDate>();

  for (const [day, timeframes] of byDay.entries()) {
    // normalize
    const normalized = [...timeframes].sort(
      (a, b) => a.from - b.from || a.to - b.to,
    );

    const key = JSON.stringify(normalized);

    if (!grouped.has(key)) {
      grouped.set(key, {
        days: [day],
        timeframes: normalized,
      });
    } else {
      grouped.get(key)!.days.push(day);
    }
  }

  const rentalDates: RentalDate[] = Array.from(grouped.values());
  if (rentalDates.length === 0) {
    rentalDates.push({
      days: [],
      timeframes: [{ from: 0, to: 24 }],
    });
  }

  // Transform infrastructure to distances
  const distances =
    locationData.infrastructure
      ?.map((infra) => ({
        id: uuidv4(),
        name: infra.name || "",
        abbreviation: infra.abbreviation || "",
        type: infra.type as
          | "motorway"
          | "railstation"
          | "airport"
          | "city-center"
          | "public-transport",
        // In der DB wird Entfernung immer in Metern gespeichert.
        // Für das Formular rechnen wir für km wieder zurück.
        unit: (infra as any).distanceUnit === "m" ? "m" : "km",
        distance:
          (infra as any).distanceUnit === "m"
            ? infra.distance || 0
            : (infra.distance || 0) / 1000,
        sorting: (infra as any).sorting ?? 0,
      }))
      // Ensure stable order by sorting ascending by sorting value
      .sort((a, b) => (a.sorting ?? 0) - (b.sorting ?? 0)) || [];

  // Transform images - separate videos, logos, and regular images
  const imageCategoriesMap = new Map<string, { id: string; name: string }>();
  const allFiles = locationData.images || [];

  const floorplansFiles = locationData.floorplans || [];
  const floorplans = floorplansFiles.map((f) => ({
    id: f.id?.toString() || crypto.randomUUID(),
    path: f.path || "",
    name: f.path?.split("/").pop() || "",
    mimetype: f.mimetype || "",
    size: f.size || 0,
  }));

  const documentsFiles = locationData.documents || [];
  const documents = documentsFiles.map((f) => ({
    id: f.id?.toString() || crypto.randomUUID(),
    path: f.path || "",
    name: f.path?.split("/").pop() || "",
    mimetype: f.mimetype || "",
    size: f.size || 0,
  }));

  const certificates =
    (locationData as any)?.certificates?.map((c: any) => ({
      file: c.file?.id
        ? {
          id: c.file.id?.toString() || crypto.randomUUID(),
          path: c.file.path || "",
          serverUrl: c.file.path
            ? c.file.path.replace(/^https?:\/\/[^\/]+/, "")
            : "",
          name: c.file.path?.split("/").pop() || c.file.name || "",
          mimetype: c.file.mimetype || "",
          size: c.file.size || 0,
        }
        : null,
      validUntil: (() => {
        const v = c.validUntil;
        if (!v) return null;
        // Normalize MySQL DATE for <input type="date">.
        if (v instanceof Date) return v.toISOString().slice(0, 10);
        if (typeof v === "string") return v.slice(0, 10);
        return null;
      })(),
      greenKey: !!c.greenKey,
      iso14001: !!c.iso14001,
      gstcHotel: !!(c as any).gstcHotel,
      active: (c as any).active ?? true,
      name: c.name || "",
    })) || [];

  // Extract videos (prefer purpose, fallback to mimetype)
  const videos = allFiles
    .filter(
      (img) =>
        img.purpose === "location_video" || img.mimetype?.startsWith("video/"),
    )
    .map((img) => ({
      id: img.id?.toString() || crypto.randomUUID(),
      path: img.path || "",
      name: img.path?.split("/").pop() || "",
      mimetype: img.mimetype || "",
      size: img.size || 0,
    }));

  // Extract logo (prefer purpose, fallback to legacy category suffix)
  const logoFile = allFiles.find(
    (img) =>
      img.purpose === "location_logo" || img.categoryName?.endsWith("_logo"),
  );
  const logo = logoFile
    ? {
      id: logoFile.id?.toString() || crypto.randomUUID(),
      path: logoFile.path || "",
      name: logoFile.path?.split("/").pop() || "",
      mimetype: logoFile.mimetype || "",
      size: logoFile.size || 0,
    }
    : null;

  // Extract contactPersonImage (prefer purpose, fallback to legacy category suffix)
  const contactPersonImageFile = allFiles.find(
    (img) =>
      img.purpose === "location_contact_person_image" ||
      img.categoryName?.endsWith("_contact_person"),
  );
  const contactPersonImage = contactPersonImageFile
    ? {
      id: contactPersonImageFile.id?.toString() || crypto.randomUUID(),
      path: contactPersonImageFile.path || "",
      name: contactPersonImageFile.path?.split("/").pop() || "",
      mimetype: contactPersonImageFile.mimetype || "",
      size: contactPersonImageFile.size || 0,
    }
    : null;

  // Filter out videos, logos, and contact person images from images
  const regularImages = allFiles.filter(
    (img) =>
      (img.purpose ? img.purpose === "location_gallery_image" : true) &&
      !img.mimetype?.startsWith("video/") &&
      !img.categoryName?.endsWith("_logo") &&
      !img.categoryName?.endsWith("_contact_person"),
  );

  const images = regularImages.map((img) => {
    let categoryId: string | null = null;
    if (img.categoryId && img.categoryName) {
      if (!imageCategoriesMap.has(img.categoryId.toString())) {
        imageCategoriesMap.set(img.categoryId.toString(), {
          id: img.categoryId.toString(),
          name: img.categoryName,
        });
      }
      categoryId = imageCategoriesMap.get(img.categoryId.toString())!.id;
    }

    return {
      id: img.id?.toString() || crypto.randomUUID(),
      path: img.path || "",
      name: img.path?.split("/").pop() || "",
      alt: img.alt || "",
      mimetype: img.mimetype || "",
      size: img.size || 0,
      sort: img.sort || 0,
      categoryId,
    };
  });

  images.sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0));
  const primaryImageId = images[0]?.id ?? null;

  const imageCategories = Array.from(imageCategoriesMap.values());

  // Transform rooms
  const rooms =
    locationData.rooms?.map((room) => {
      const roomImageCategoriesMap = new Map<
        string,
        { id: string; name: string }
      >();
      const roomImages =
        room.images?.map((img) => {
          let categoryId: string | null = null;
          if (img.categoryId && img.categoryName) {
            if (!roomImageCategoriesMap.has(img.categoryId.toString())) {
              roomImageCategoriesMap.set(img.categoryId.toString(), {
                id: img.categoryId.toString(),
                name: img.categoryName,
              });
            }
            categoryId = roomImageCategoriesMap.get(
              img.categoryId.toString(),
            )!.id;
          }

          return {
            id: img.id?.toString() || crypto.randomUUID(),
            path: img.path || "",
            name: img.path?.split("/").pop() || "",
            alt: img.alt || "",
            mimetype: img.mimetype || "",
            size: img.size || 0,
            sort: img.sort || 0,
            categoryId,
          };
        }) || [];

      return {
        id: room.id,
        name: room.nameForm
          ? normalizeBilingual(room.nameForm)
          : { german: room.name || "", english: "" },
        areaType: (room.areaType as "indoor" | "outdoor" | "mixed") || "indoor",
        separable: room.separable || false,
        roomShapeId: room.roomShapeId ?? null,
        shortDescription: normalizeBilingual(room.shortDescription),
        longDescription: normalizeBilingual(room.longDescription),
        maxLength: convertCmToMeters(room.maxLength),
        maxWidth: convertCmToMeters(room.maxWidth),
        maxHeight: convertCmToMeters(room.maxHeight),
        maxArea: convertCmToMeters(room.maxArea),
        seatingBanquet: room.seatingBanquet || 0,
        seatingBlock: room.seatingBlock || 0,
        seatingParliamentary: room.seatingParliamentary || 0,
        seatingRow: room.seatingRow || 0,
        seatingTable: room.seatingTable || 0,
        seatingUShape: room.seatingUShape || 0,
        seatingParty: room.seatingParty || 0,
        seatingStanding: room.seatingStanding || 0,
        maxCapacity: (room as any).maxCapacity || 0,
        priceIsPublic: room.priceIsPublic ?? true,
        rateDaily: room.rateDaily || 0,
        rateDailyHourLimit: room.rateDailyHourLimit || 0,
        rateHourly: room.rateHourly || 0,
        rateHourlyMinimumHours: room.rateHourlyMinimumHours || 0,
        minimumConsumptionPrice: room.minimumConsumptionPrice || 0,
        minimumConsumptionPersons: room.minimumConsumptionPersons || 0,
        consumptionFlatRatePerPerson: room.consumptionFlatRatePerPerson || 0,
        consumptionFlatRateMinimumPersons:
          room.consumptionFlatRateMinimumPersons || 0,
        additionalPricingInformation: normalizeBilingual(
          room.additionalPricingInformation,
        ),
        cleaningCosts: room.cleaningCosts || 0,
        additionalCleaningCostsInformation: normalizeBilingual(
          room.additionalCleaningCostsInformation,
        ),
        additionalCosts: room.additionalCosts || 0,
        additionalCostsInformation: normalizeBilingual(
          room.additionalCostsInformation,
        ),
        groundLevel: room.groundLevel || false,
        accessible: room.accessible || false,
        daylight: room.daylight || false,
        accessibleForCars: room.accessibleForCars || false,
        lighting: room.lighting || false,
        dimmableLighting: room.dimmableLighting || false,
        windows: room.windows || false,
        blackoutCapable: room.blackoutCapable || false,
        heating: room.heating || false,
        airConditioning: room.airConditioning || false,
        furnished: room.furnished || false,
        waterSupply: room.waterSupply ?? false,
        acPower: room.acPower || false,
        threePhasePower: room.threePhasePower || false,
        stage: room.stage || false,
        lectern: room.lectern || false,
        chairs: room.chairs || false,
        videoTechnology: room.videoTechnology || false,
        soundSystem: room.soundSystem || false,
        publicAddressSystem: room.publicAddressSystem || false,
        interpreterSystem: room.interpreterSystem || false,
        projectionTechnology: room.projectionTechnology || false,
        videoConference: room.videoConference || false,
        wifi: room.wifi || false,
        lan: room.lan || false,
        telephone: room.telephone ?? false,
        tvConnection: room.tvConnection ?? false,
        internet: room.internet ?? false,
        additionalEquipmentInformation: normalizeBilingual(
          room.additionalEquipmentInformation,
        ),
        onlyA1: room.onlyA1 || false,
        onlyB1: room.onlyB1 || false,
        noPyrotechnics: room.noPyrotechnics || false,
        noOpenFire: room.noOpenFire || false,
        absoluteSmokingBan: room.absoluteSmokingBan || false,
        additionalFireProtectionInformation: normalizeBilingual(
          room.additionalFireProtectionInformation,
        ),
        imageCategories: Array.from(roomImageCategoriesMap.values()),
        images: roomImages,
        roomInformationAdditionalAttributes:
          locationData.additionalAttributes.filter(
            (a) =>
              a.purpose === "location_room_information_attribute" &&
              a.roomId === room.id,
          ) || [],
        roomFacilityAdditionalAttributes:
          locationData.additionalAttributes.filter(
            (a) =>
              a.purpose === "location_room_facility_attribute" &&
              a.roomId === room.id,
          ) || [],
        roomEventTechnologyAdditionalAttributes:
          locationData.additionalAttributes.filter(
            (a) =>
              a.purpose === "location_room_event_technology_attribute" &&
              a.roomId === room.id,
          ) || [],
        roomCommunicationTechnologyAdditionalAttributes:
          locationData.additionalAttributes.filter(
            (a) =>
              a.purpose ===
              "location_room_communication_technology_attribute" &&
              a.roomId === room.id,
          ) || [],
        roomFlooringAdditionalAttributes:
          locationData.additionalAttributes.filter(
            (a) =>
              a.purpose === "location_room_flooring_attribute" &&
              a.roomId === room.id,
          ) || [],
        roomDecorationAdditionalAttributes:
          locationData.additionalAttributes.filter(
            (a) =>
              a.purpose === "location_room_decoration_attribute" &&
              a.roomId === room.id,
          ) || [],
        roomFireProtectionAdditionalAttributes:
          locationData.additionalAttributes.filter(
            (a) =>
              a.purpose === "location_room_fire_protection_attribute" &&
              a.roomId === room.id,
          ) || [],
        roomSmokingAdditionalAttributes:
          locationData.additionalAttributes.filter(
            (a) =>
              a.purpose === "location_room_smoking_attribute" &&
              a.roomId === room.id,
          ) || [],
        roomAdditionalCostsAdditionalAttributes:
          locationData.additionalAttributes.filter(
            (a) =>
              a.purpose === "location_room_additional_costs_attribute" &&
              a.roomId === room.id,
          ) || [],
      };
    }) || [];

  // Transform hotelRoomInstances
  const hotelRoomInstances =
    locationData.hotelRoomInstances?.map((hotelRoom) => {
      const hotelRoomImageCategoriesMap = new Map<
        string,
        { id: string; name: string }
      >();
      const hotelRoomImages =
        hotelRoom.images?.map((img) => {
          let categoryId: string | null = null;
          if (img.categoryId && img.categoryName) {
            if (!hotelRoomImageCategoriesMap.has(img.categoryId.toString())) {
              hotelRoomImageCategoriesMap.set(img.categoryId.toString(), {
                id: img.categoryId.toString(),
                name: img.categoryName,
              });
            }
            categoryId = hotelRoomImageCategoriesMap.get(
              img.categoryId.toString(),
            )!.id;
          }

          return {
            id: img.id?.toString() || crypto.randomUUID(),
            path: img.path || "",
            name: img.path?.split("/").pop() || "",
            alt: img.alt || "",
            mimetype: img.mimetype || "",
            size: img.size || 0,
            sort: img.sort || 0,
            categoryId,
          };
        }) || [];

      return {
        id: hotelRoom.id,
        name: normalizeBilingual(hotelRoom.name),
        description: normalizeBilingual(hotelRoom.description),
        imageCategories: Array.from(hotelRoomImageCategoriesMap.values()),
        images: hotelRoomImages,
      };
    }) || [];

  // Transform outsideAreas
  const outsideAreas =
    locationData.outsideAreas?.map((o) => {
      const outsideAreaImageCategoriesMap = new Map<
        string,
        { id: string; name: string }
      >();
      const outsideAreaImages =
        o.images?.map((img) => {
          let categoryId: string | null = null;
          if (img.categoryId && img.categoryName) {
            if (!outsideAreaImageCategoriesMap.has(img.categoryId.toString())) {
              outsideAreaImageCategoriesMap.set(img.categoryId.toString(), {
                id: img.categoryId.toString(),
                name: img.categoryName,
              });
            }
            categoryId = outsideAreaImageCategoriesMap.get(
              img.categoryId.toString(),
            )!.id;
          }

          return {
            id: img.id?.toString() || crypto.randomUUID(),
            path: img.path || "",
            name: img.path?.split("/").pop() || "",
            alt: img.alt || "",
            mimetype: img.mimetype || "",
            size: img.size || 0,
            sort: img.sort || 0,
            categoryId,
          };
        }) || [];

      return {
        id: o.id,
        name: normalizeBilingual(o.name),
        areaType: (o.areaType as "indoor" | "outdoor" | "mixed") || "indoor",
        separable: o.separable || false,
        shapeGallery: o.shapeGallery || false,
        shortDescription: normalizeBilingual(o.shortDescription),
        longDescription: normalizeBilingual(o.longDescription),
        maxLength: convertCmToMeters(o.maxLength),
        maxWidth: convertCmToMeters(o.maxWidth),
        maxHeight: convertCmToMeters(o.maxHeight),
        maxArea: convertCmToMeters(o.maxArea),
        seatingBanquet: o.seatingBanquet || 0,
        seatingBlock: o.seatingBlock || 0,
        seatingParliamentary: o.seatingParliamentary || 0,
        seatingRow: o.seatingRow || 0,
        seatingTable: o.seatingTable || 0,
        seatingUShape: o.seatingUShape || 0,
        seatingParty: o.seatingParty || 0,
        seatingStanding: o.seatingStanding || 0,
        priceIsPublic: o.priceIsPublic ?? true,
        rateDaily: o.rateDaily || 0,
        rateDailyHourLimit: o.rateDailyHourLimit || 0,
        rateHourly: o.rateHourly || 0,
        rateHourlyMinimumHours: o.rateHourlyMinimumHours || 0,
        minimumConsumptionPrice: o.minimumConsumptionPrice || 0,
        minimumConsumptionPersons: o.minimumConsumptionPersons || 0,
        consumptionFlatRatePerPerson: o.consumptionFlatRatePerPerson || 0,
        consumptionFlatRateMinimumPersons:
          o.consumptionFlatRateMinimumPersons || 0,
        additionalPricingInformation: normalizeBilingual(
          o.additionalPricingInformation,
        ),
        includedInRent: (o as { includedInRent?: boolean }).includedInRent ?? true,
        accessible: o.accessible || false,
        covered: o.covered || false,
        withSeating: o.withSeating || false,
        withWaterAccess: o.withWaterAccess || false,
        imageCategories: Array.from(outsideAreaImageCategoriesMap.values()),
        images: outsideAreaImages,
        outsideAreaAdditionalAttributes:
          locationData.additionalAttributes.filter(
            (a) =>
              a.purpose === "location_outside_area_attribute" &&
              a.outsideAreaId === o.id,
          ) || [],
      };
    }) || [];

  return {
    plan: locationData.plan || "superior",
    billingInterval:
      (locationData.billingInterval as "half-year" | "full-year") ||
      "full-year",
    locationName: locationData.nameForm
      ? normalizeBilingual(locationData.nameForm)
      : {
        german: locationData.name || "",
        english: "",
      },
    street: locationData.street || "",
    houseNumber: locationData.houseNumber || "",
    zipCode: locationData.postalCode || "",
    city: locationData.city || "",
    country: locationData.country || "",
    state: locationData.state ?? "",
    coordinates: {
      lat: locationData.lat || 0,
      lng: locationData.lng || 0,
    },
    locationTypes: locationData.locationTypes?.map((lt) => lt.id) || [],
    eventTypes:
      locationData.eventTypes
        ?.map((et) => et.id)
        .filter((id): id is number => id !== null) || [],
    onlyPredefinedOccasions: locationData.onlyPredefinedOccasions || false,
    minGuests: locationData.minGuests,
    minBudgetTotal: locationData.minBudgetTotal,
    minBudgetPerPerson: locationData.minBudgetPerPerson,
    currency: (locationData.currency as "EUR" | "CHF" | "USD") || "EUR",
    markTelephoneRequired: locationData.markTelephoneRequired || false,
    languages: Array.isArray(locationData.languages)
      ? locationData.languages
      : typeof locationData.languages === "string"
        ? (JSON.parse(locationData.languages) as string[])
        : ["german"],
    description: normalizeBilingual(locationData.descriptionForm),
    descriptionLong: normalizeBilingual(locationData.descriptionLong),
    imageCategories,
    primaryImageId,
    images,
    logo,
    contactPersonImage,
    videos,
    virtualTour: locationData.virtualTour,
    rentalOutOfHoursPossible: locationData.rentalOutOfHoursPossible || false,
    rentalDates,
    additionalRentalInformation: normalizeBilingual(
      locationData.additionalRentalInformation,
    ),
    priceMode: (locationData.priceMode as "net" | "gross") || "net",
    withVat: locationData.withVat || false,
    vatAmount: (locationData.vatAmount as 19) || 19,
    priceIsPublic: locationData.priceIsPublic ?? false,
    rateDaily: locationData.rateDaily || 0,
    rateDailyHourLimit: locationData.rateDailyHourLimit || 0,
    rateHourly: locationData.rateHourly || 0,
    rateHourlyMinimumHours: locationData.rateHourlyMinimumHours || 0,
    minimumConsumptionPrice: locationData.minimumConsumptionPrice || 0,
    minimumConsumptionPersons: locationData.minimumConsumptionPersons || 0,
    consumptionFlatRatePerPerson:
      locationData.consumptionFlatRatePerPerson || 0,
    consumptionFlatRateMinimumPersons:
      locationData.consumptionFlatRateMinimumPersons || 0,
    additionalPricingInformation: normalizeBilingual(
      locationData.additionalPricingInformation,
    ),
    cleaningCosts: locationData.cleaningCosts || 0,
    additionalCleaningCostsInformation: normalizeBilingual(
      locationData.additionalCleaningCostsInformation,
    ),
    additionalCosts: (locationData as any).additionalCosts ?? 0,
    additionalCostsInformation: normalizeBilingual(
      locationData.additionalCostsInformation,
    ),
    openingYear: locationData.openingYear,
    yearOfRenovation: locationData.yearOfRenovation,
    areaType:
      (locationData.areaType as "indoor" | "outdoor" | "mixed") || "indoor",
    infraTypes:
      locationData.infraTypes
        ?.map((it) => it.id)
        .filter((id): id is number => id !== null) || [],
    distances,
    locationShortDescription: normalizeBilingual(
      locationData.locationShortDescription,
    ),
    maxLength: convertCmToMeters(locationData.maxLength),
    maxWidth: convertCmToMeters(locationData.maxWidth),
    maxHeight: convertCmToMeters(locationData.maxHeight),
    maxArea: convertCmToMeters(locationData.maxArea),
    indoorAreaTotal: convertCmToMeters(locationData.indoorAreaTotal),
    outsideAreaSum: convertCmToMeters(locationData.outsideAreaSum),
    seatingBanquet: locationData.seatingBanquet || 0,
    seatingBlock: locationData.seatingBlock || 0,
    seatingParliamentary: locationData.seatingParliamentary || 0,
    seatingRow: locationData.seatingRow || 0,
    seatingTable: locationData.seatingTable || 0,
    seatingUShape: locationData.seatingUShape || 0,
    seatingParty: locationData.seatingParty || 0,
    seatingStanding: locationData.seatingStanding || 0,
    maxCapacity: (locationData as any).maxCapacity || 0,
    minimumCapacity: locationData.minimumCapacity || 0,
    stage: locationData.stage || false,
    stageDepth: convertCmToMeters(locationData.stageDepth),
    stageLength: convertCmToMeters(locationData.stageLength),
    stageWidth: convertCmToMeters(locationData.stageWidth),
    stageHeight: convertCmToMeters(locationData.stageHeight),
    stageArea: convertCmToMeters(locationData.stageArea),
    stageFixed: locationData.stageFixed || false,
    additionalStageInformation: normalizeBilingual(
      locationData.additionalStageInformation,
    ),
    catering: (locationData.catering === "multiplePartner"
      ? "multiplePartner"
      : locationData.catering === "permanentPartner"
        ? "permanentPartner"
        : locationData.catering === "freeChoicePaid"
          ? "freeChoicePaid"
          : "free") as
      | "free"
      | "permanentPartner"
      | "freeChoicePaid"
      | "multiplePartner",
    tech: (locationData.tech === "multiplePartner"
      ? "multiplePartner"
      : locationData.tech === "permanentPartner"
        ? "permanentPartner"
        : locationData.tech === "freeChoicePaid"
          ? "freeChoicePaid"
          : "free") as
      | "free"
      | "permanentPartner"
      | "freeChoicePaid"
      | "multiplePartner",
    contractDetails: normalizeBilingual(locationData.contractDetails),
    staff: locationData.staff || false,
    kitchen: locationData.kitchen || false,
    cateringRoom: locationData.cateringRoom || false,
    tableware: locationData.tableware || false,
    waterSupply: locationData.waterSupply || false,
    v230: locationData.v230 || false,
    v110: locationData.v110 || false,
    a16: locationData.a16 || false,
    a32: locationData.a32 || false,
    a63: locationData.a63 || false,
    a128: locationData.a128 || false,
    furnished: (locationData.furnishedForm === "yes"
      ? "yes"
      : locationData.furnishedForm === "bookable"
        ? "bookable"
        : "no") as "yes" | "no" | "bookable",
    amountOfChairs: locationData.amountOfChairs || 0,
    amountOfTables: locationData.amountOfTables || 0,
    additionalFurnitureInformation: normalizeBilingual(
      locationData.additionalFurnitureInformation,
    ),
    accessibleForTrucks: locationData.accessibleForTrucks || false,
    accessibleForCars: locationData.accessibleForCars || false,
    freightElevator: locationData.freightElevator || false,
    loadingDock: locationData.loadingDock || false,
    locationLogisticsAdditionalAttributes: locationData.additionalAttributes.filter(
      (a) => a.purpose === "location_logistics_attribute",
    ) || [],
    additionalLoadingDockInformation: normalizeBilingual(
      locationData.additionalLoadingDockInformation,
    ),
    parkingLots: (locationData.parkingLots === "yes"
      ? "yes"
      : locationData.parkingLots === "noOwn"
        ? "noOwn"
        : "no") as "yes" | "no" | "noOwn",
    parkingLotRoof: locationData.parkingLotRoof || false,
    parkingSpaceCount: locationData.parkingSpaceCount || 0,
    coveredParkingSpaceCount: locationData.coveredParkingSpaceCount || 0,
    additionalParkingSpaceInformation: normalizeBilingual(
      locationData.additionalParkingSpaceInformation,
    ),
    accommodations: locationData.accommodations || false,
    hotelRooms: locationData.hotelRooms || 0,
    hotelSingleRooms: locationData.hotelSingleRooms || 0,
    hotelDoubleRooms: locationData.hotelDoubleRooms || 0,
    hotelDorms: locationData.hotelDorms || 0,
    freeWifi: locationData.freeWifi || false,
    freeLan: locationData.freeLan || false,
    hotelStars: locationData.hotelStars || 0,
    hotelDescription: normalizeBilingual(locationData.hotelDescription),
    hotelTypes: locationData.hotelTypes?.map((ht) => ht.id) || [],
    reception24hours: locationData.reception24hours || false,
    wakeUpService: locationData.wakeUpService || false,
    roomService: locationData.roomService || false,
    conferenceOffice: locationData.conferenceOffice || false,
    hotelSafe: locationData.hotelSafe || false,
    wifi: locationData.wifi || false,
    lan: locationData.lan || false,
    roomsWithShowers: locationData.roomsWithShowers || false,
    roomsWithBath: locationData.roomsWithBath || false,
    roomsWithAirConditioning: locationData.roomsWithAirConditioning || false,
    sightseeing: locationData.sightseeing || false,
    golf: locationData.golf || false,
    sailing: locationData.sailing || false,
    tennis: locationData.tennis || false,
    ski: locationData.ski || false,
    spa: locationData.spa || false,
    swimmingIndoor: locationData.swimmingIndoor || false,
    massage: locationData.massage || false,
    swimmingOutdoor: locationData.swimmingOutdoor || false,
    additionalLeisureInformation: normalizeBilingual(
      locationData.additionalLeisureInformation,
    ),
    contactPersonFirstName: locationData.contactPersonFirstName || "",
    contactPersonLastName: locationData.contactPersonLastName || "",
    contactPersonPosition: locationData.contactPersonPosition,
    contactPersonEmail: locationData.contactPersonEmail || "",
    contactPersonPhone: locationData.contactPersonPhone,
    contactEmail: locationData.contactEmail || "",
    ccEmails: Array.isArray(locationData.ccEmails) ? locationData.ccEmails : [],
    contactTelephone: locationData.contactTelephone ?? "",
    website: locationData.website ?? null,
    websites: (() => {
      const raw = (locationData as any).websites;
      let parsed: { label: string; url: string }[] = [];
      if (Array.isArray(raw)) {
        parsed = raw as { label: string; url: string }[];
      } else if (typeof raw === "string" && raw.trim()) {
        try {
          const tmp = JSON.parse(raw);
          if (Array.isArray(tmp)) {
            parsed = tmp as { label: string; url: string }[];
          }
        } catch {
          // ignore JSON parse errors and fall back to legacy website
        }
      }
      if (parsed.length > 0) return parsed;
      return locationData.website
        ? [{ label: "Hauptwebsite", url: locationData.website }]
        : [];
    })(),
    socialLinkedin: locationData.socialLinkedin ?? null,
    socialInstagram: locationData.socialInstagram ?? null,
    socialProfiles: (() => {
      const DEFAULTS = ["LinkedIn", "Instagram", "Facebook", "TikTok"] as const;
      const raw = (locationData as any).socialProfiles;
      let parsed: { label: string; url: string }[] = [];
      if (Array.isArray(raw)) {
        parsed = raw as { label: string; url: string }[];
      } else if (typeof raw === "string" && raw.trim()) {
        try {
          const tmp = JSON.parse(raw);
          if (Array.isArray(tmp)) parsed = tmp as { label: string; url: string }[];
        } catch {
          // ignore
        }
      }

      const normalized = (parsed ?? [])
        .map((p) => ({
          label: (p?.label ?? "").toString().trim(),
          url: (p?.url ?? "").toString().trim(),
        }))
        .filter((p) => p.label || p.url);

      const byLabel = new Map(
        normalized
          .filter((p) => p.label)
          .map((p) => [p.label.toLowerCase(), p]),
      );

      const defaults = DEFAULTS.map((label) => ({
        label,
        url: byLabel.get(label.toLowerCase())?.url ?? "",
      }));

      const extras = normalized.filter((p) => {
        const l = p.label.toLowerCase();
        return !DEFAULTS.some((d) => d.toLowerCase() === l);
      });

      // legacy fallback if nothing else exists
      if (defaults.every((d) => !d.url) && extras.length === 0) {
        if (locationData.socialLinkedin)
          defaults[0].url = locationData.socialLinkedin;
        if (locationData.socialInstagram)
          defaults[1].url = locationData.socialInstagram;
      }

      return [...defaults, ...extras];
    })(),
    imprintCompany: locationData.imprintCompany || "",
    imprintFirstName: locationData.imprintFirstName || "",
    imprintLastName: locationData.imprintLastName || "",
    imprintEmail: locationData.imprintEmail || "",
    imprintStreet: locationData.imprintStreet || "",
    imprintHouseNumber: locationData.imprintHouseNumber || "",
    imprintZipCode: locationData.imprintZipCode || "",
    imprintCity: locationData.imprintCity || "",
    imprintCountry: locationData.imprintCountry || "",
    faq: Array.isArray((locationData as any).faq)
      ? (locationData as any).faq.map((item: any) => ({
        question: normalizeBilingual(item?.question),
        answer: normalizeBilingual(item?.answer),
      }))
      : [],
    highlightLabel: (locationData as any).highlightLabel ?? "",
    moreThanOneRoom: (locationData.rooms?.length || 0) > 0,
    onlyWholeLocationBookable: false, // TODO: Add to getLocation if needed
    rooms,
    hotelRoomInstances,
    hasOutsideAreas: (outsideAreas?.length || 0) > 0, // TODO: Update when getLocation returns outsideAreas
    outsideAreas,
    floorplans,
    documents,
    certificates,
    accessible: locationData.accessible || false,
    blackoutCapable: locationData.blackoutCapable || false,
    daylight: locationData.daylight || false,
    additionalContractBindings: locationData.additionalContractBindings || [],
    locationAdditionalAttributes:
      locationData.additionalAttributes.filter(
        (a) => a.purpose === "location_attribute",
      ) || [],
    cateringAdditionalAttributes:
      locationData.additionalAttributes.filter(
        (a) => a.purpose === "location_catering_attribute",
      ) || [],
    lightingTechnologyAdditionalAttributes: (() => {
      const base =
        locationData.additionalAttributes.filter(
          (a) => a.purpose === "location_lighting_technology_attribute",
        ) || [];
      if (base.length > 0) return base;
      if (locationData.lighting)
        return [
          syntheticAttr(
            "Lichttechnik vorhanden",
            "location_lighting_technology_attribute",
          ),
        ];
      return [];
    })(),
    presentationTechnologyAdditionalAttributes: (() => {
      const base =
        locationData.additionalAttributes.filter(
          (a) => a.purpose === "location_presentation_technology_attribute",
        ) || [];
      if (base.length > 0) return base;
      if (locationData.presentationAndVideo)
        return [
          syntheticAttr(
            "Präsentations- & Videotechnik vorhanden",
            "location_presentation_technology_attribute",
          ),
        ];
      return [];
    })(),
    soundTechnologyAdditionalAttributes: (() => {
      const base =
        locationData.additionalAttributes.filter(
          (a) => a.purpose === "location_sound_technology_attribute",
        ) || [];
      if (base.length > 0) return base;
      if (locationData.sound)
        return [
          syntheticAttr(
            "Tontechnik vorhanden",
            "location_sound_technology_attribute",
          ),
        ];
      return [];
    })(),
    heatingAndAirConditioningTechnologyAdditionalAttributes: (() => {
      const base =
        locationData.additionalAttributes.filter(
          (a) =>
            a.purpose ===
            "location_heating_and_air_conditioning_technology_attribute",
        ) || [];
      if (base.length > 0) return base;
      if (locationData.heatingAndAirConditioning)
        return [
          syntheticAttr(
            "Heiz- & Klimatechnik vorhanden",
            "location_heating_and_air_conditioning_technology_attribute",
          ),
        ];
      return [];
    })(),
    riggingAdditionalAttributes: (() => {
      const base =
        locationData.additionalAttributes.filter(
          (a) => a.purpose === "location_rigging_attribute",
        ) || [];
      if (base.length > 0) return base;
      if (locationData.rigging)
        return [
          syntheticAttr(
            "Rigging (Traverse) vorhanden",
            "location_rigging_attribute",
          ),
        ];
      return [];
    })(),
    specialTechnologyAdditionalAttributes:
      locationData.additionalAttributes.filter(
        (a) => a.purpose === "location_special_technology_attribute",
      ) || [],
    internetAvailabilityAdditionalAttributes: (() => {
      const base =
        locationData.additionalAttributes.filter(
          (a) => a.purpose === "location_internet_availability_attribute",
        ) || [];
      if (base.length > 0) return base;
      const parts: z.infer<typeof import("./types").AdditionalAttributeSchema>[] = [];
      const conn: string[] = [];
      if (locationData.connection5G) conn.push("5G");
      if (locationData.connection4G) conn.push("4G");
      if (locationData.connection3G) conn.push("3G");
      if (locationData.connection2G) conn.push("2G");
      if (conn.length)
        parts.push(
          syntheticAttr(
            `Mobilfunk: ${conn.join(", ")}`,
            "location_internet_availability_attribute",
          ),
        );
      if (locationData.bandWidth && locationData.bandWidth > 0)
        parts.push(
          syntheticAttr(
            `Bandbreite: ${locationData.bandWidth} Mbit/s`,
            "location_internet_availability_attribute",
          ),
        );
      return parts;
    })(),
    additionalTechnologyInformation: normalizeBilingual(
      (locationData as any).additionalTechnologyInformation,
    ),
    equipmentAdditionalAttributes: (() => {
      const base =
        locationData.additionalAttributes.filter(
          (a) => a.purpose === "location_equipment_attribute",
        ) || [];
      const add = (
        label: string,
        condition: boolean | undefined | null,
      ) => {
        if (!condition) return;
        if (base.some((a) => (a as any).name?.german === label)) return;
        base.push(
          syntheticAttr(label, "location_equipment_attribute") as any,
        );
      };
      add("Raucher-Bereich", locationData.smokingArea);
      add("keine Lärmschutzauflagen", locationData.noNoiseControlRequirements);
      add("Backstage-Bereich", locationData.backstageArea);
      add("Künstlergarderoben", locationData.artistDressingRooms);
      add("Dusche", locationData.showers);
      add("Tanzfläche", locationData.danceFloor);
      return base;
    })(),
    furnitureAndDecorationAdditionalAttributes: (() => {
      const base =
        locationData.additionalAttributes.filter(
          (a) => a.purpose === "location_furniture_and_decoration_attribute",
        ) || [];
      if (base.length > 0) return base;
      const furnished =
        locationData.furnishedForm === "yes" ||
        locationData.furnishedForm === "bookable";
      const chairs = locationData.amountOfChairs ?? 0;
      const tables = locationData.amountOfTables ?? 0;
      if (furnished && chairs > 0)
        base.push(
          syntheticAttr(
            `Stühle: ${chairs}`,
            "location_furniture_and_decoration_attribute",
          ) as any,
        );
      if (furnished && tables > 0)
        base.push(
          syntheticAttr(
            `Tische: ${tables}`,
            "location_furniture_and_decoration_attribute",
          ) as any,
        );
      return base;
    })(),
    sanitaryFacilitiesAdditionalAttributes: (() => {
      const base =
        locationData.additionalAttributes.filter(
          (a) => a.purpose === "location_sanitary_facility_attribute",
        ) || [];
      if (base.length > 0) return base;
      if (locationData.wcFemale)
        base.push(
          syntheticAttr(
            "WC Damen vorhanden",
            "location_sanitary_facility_attribute",
          ) as any,
        );
      if (locationData.wcMale)
        base.push(
          syntheticAttr(
            "WC Herren vorhanden",
            "location_sanitary_facility_attribute",
          ) as any,
        );
      if (locationData.wcAccessible)
        base.push(
          syntheticAttr(
            "Barrierefreies WC vorhanden",
            "location_sanitary_facility_attribute",
          ) as any,
        );
      return base;
    })(),
    additionalSanitaryInformation: normalizeBilingual(
      (locationData as any).additionalSanitaryInformation,
    ),
    hotelServiceAdditionalAttributes:
      locationData.additionalAttributes.filter(
        (a) => a.purpose === "location_hotel_service_attribute",
      ) || [],
    hotelFacilityAdditionalAttributes:
      locationData.additionalAttributes.filter(
        (a) => a.purpose === "location_hotel_facility_attribute",
      ) || [],
    hotelRoomFacilityAdditionalAttributes:
      locationData.additionalAttributes.filter(
        (a) => a.purpose === "location_hotel_room_facility_attribute",
      ) || [],
    leisureTimeAdditionalAttributes:
      locationData.additionalAttributes.filter(
        (a) => a.purpose === "location_leisure_time_attribute",
      ) || [],
    sportAdditionalAttributes:
      locationData.additionalAttributes.filter(
        (a) => a.purpose === "location_sport_attribute",
      ) || [],
    wellnessAdditionalAttributes:
      locationData.additionalAttributes.filter(
        (a) => a.purpose === "location_wellness_attribute",
      ) || [],
    additionalCostsAdditionalAttributes:
      locationData.additionalAttributes.filter(
        (a) => a.purpose === "location_additional_costs_attribute",
      ) || [],
  } as z.infer<typeof InsertLocationSchema>;
}

/** Backfill legacy location columns into additional_attributes for migration. Returns rows to insert per location. */
export function getLegacyEquipmentBackfillAttributes(locationData: {
  lighting?: boolean | null;
  presentationAndVideo?: boolean | null;
  sound?: boolean | null;
  heatingAndAirConditioning?: boolean | null;
  rigging?: boolean | null;
  connection5G?: boolean | null;
  connection4G?: boolean | null;
  connection3G?: boolean | null;
  connection2G?: boolean | null;
  bandWidth?: number | null;
  wcFemale?: boolean | null;
  wcMale?: boolean | null;
  wcAccessible?: boolean | null;
  smokingArea?: boolean | null;
  artistDressingRooms?: boolean | null;
  noNoiseControlRequirements?: boolean | null;
  showers?: boolean | null;
  backstageArea?: boolean | null;
  danceFloor?: boolean | null;
  furnishedForm?: string | null;
  amountOfChairs?: number | null;
  amountOfTables?: number | null;
  additionalAttributes?: Array<{
    purpose: string;
    name?: { german?: string; english?: string } | null;
  }>;
}) {
  const attrs = locationData.additionalAttributes || [];
  const has = (p: string) => attrs.some((a) => a.purpose === p);
  const out: Array<{
    purpose: string;
    name: { german: string; english: string };
  }> = [];

  if (!has("location_lighting_technology_attribute") && locationData.lighting)
    out.push({
      purpose: "location_lighting_technology_attribute",
      name: { german: "Lichttechnik vorhanden", english: "" },
    });
  if (
    !has("location_presentation_technology_attribute") &&
    locationData.presentationAndVideo
  )
    out.push({
      purpose: "location_presentation_technology_attribute",
      name: { german: "Präsentations- & Videotechnik vorhanden", english: "" },
    });
  if (!has("location_sound_technology_attribute") && locationData.sound)
    out.push({
      purpose: "location_sound_technology_attribute",
      name: { german: "Tontechnik vorhanden", english: "" },
    });
  if (
    !has("location_heating_and_air_conditioning_technology_attribute") &&
    locationData.heatingAndAirConditioning
  )
    out.push({
      purpose: "location_heating_and_air_conditioning_technology_attribute",
      name: { german: "Heiz- & Klimatechnik vorhanden", english: "" },
    });
  if (!has("location_rigging_attribute") && locationData.rigging)
    out.push({
      purpose: "location_rigging_attribute",
      name: { german: "Rigging (Traverse) vorhanden", english: "" },
    });

  const connParts: string[] = [];
  if (locationData.connection5G) connParts.push("5G");
  if (locationData.connection4G) connParts.push("4G");
  if (locationData.connection3G) connParts.push("3G");
  if (locationData.connection2G) connParts.push("2G");
  if (
    !has("location_internet_availability_attribute") &&
    (connParts.length > 0 || (locationData.bandWidth && locationData.bandWidth > 0))
  ) {
    if (connParts.length)
      out.push({
        purpose: "location_internet_availability_attribute",
        name: { german: `Mobilfunk: ${connParts.join(", ")}`, english: "" },
      });
    if (locationData.bandWidth && locationData.bandWidth > 0)
      out.push({
        purpose: "location_internet_availability_attribute",
        name: {
          german: `Bandbreite: ${locationData.bandWidth} Mbit/s`,
          english: "",
        },
      });
  }

  const existingEquip = attrs.filter(
    (a) => a.purpose === "location_equipment_attribute",
  );
  const hasEquipLabel = (label: string) =>
    existingEquip.some((a) => (a as any).name?.german === label);
  const equip = (label: string) => {
    if (hasEquipLabel(label)) return;
    out.push({
      purpose: "location_equipment_attribute",
      name: { german: label, english: "" },
    });
  };
  if (locationData.smokingArea) equip("Raucher-Bereich");
  if (locationData.noNoiseControlRequirements)
    equip("keine Lärmschutzauflagen");
  if (locationData.backstageArea) equip("Backstage-Bereich");
  if (locationData.artistDressingRooms) equip("Künstlergarderoben");
  if (locationData.showers) equip("Dusche");
  if (locationData.danceFloor) equip("Tanzfläche");

  if (!has("location_sanitary_facility_attribute")) {
    if (locationData.wcFemale)
      out.push({
        purpose: "location_sanitary_facility_attribute",
        name: { german: "WC Damen vorhanden", english: "" },
      });
    if (locationData.wcMale)
      out.push({
        purpose: "location_sanitary_facility_attribute",
        name: { german: "WC Herren vorhanden", english: "" },
      });
    if (locationData.wcAccessible)
      out.push({
        purpose: "location_sanitary_facility_attribute",
        name: { german: "Barrierefreies WC vorhanden", english: "" },
      });
  }

  if (!has("location_furniture_and_decoration_attribute")) {
    const furnished =
      locationData.furnishedForm === "yes" ||
      locationData.furnishedForm === "bookable";
    const chairs = locationData.amountOfChairs ?? 0;
    const tables = locationData.amountOfTables ?? 0;
    if (furnished && chairs > 0)
      out.push({
        purpose: "location_furniture_and_decoration_attribute",
        name: { german: `Stühle: ${chairs}`, english: "" },
      });
    if (furnished && tables > 0)
      out.push({
        purpose: "location_furniture_and_decoration_attribute",
        name: { german: `Tische: ${tables}`, english: "" },
      });
  }

  return out;
}
